import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiCheckCircle, FiTrendingUp, FiGlobe, FiCpu, FiAlertTriangle, FiClock, FiArrowRight, FiMail, FiCheck } = FiIcons;

const QuinformationPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [digitSequence, setDigitSequence] = useState('');
  const [codeStream, setCodeStream] = useState('');
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [colorIndex, setColorIndex] = useState(0);

  // Define the color scheme for animation
  const colors = {
    orange: '#FF6A00',
    blue: '#00F0FF',
    purple: '#D94EFF'
  };

  // Calculate the color based on the animation
  const currentColor = () => {
    if (colorIndex < 33) {
      return colors.orange;
    } else if (colorIndex < 66) {
      return colors.blue;
    } else {
      return colors.purple;
    }
  };

  // Animated color effect
  useEffect(() => {
    const interval = setInterval(() => {
      setColorIndex(prev => (prev + 1) % 100);
    }, 60); // Update frequently for smooth transitions
    
    return () => clearInterval(interval);
  }, []);

  // Interpolate between colors for smooth transitions
  const getInterpolatedColor = () => {
    // Create smooth transitions between the three colors
    if (colorIndex < 33) {
      // Transition from orange to blue
      const ratio = colorIndex / 33;
      return interpolateColor(colors.orange, colors.blue, ratio);
    } else if (colorIndex < 66) {
      // Transition from blue to purple
      const ratio = (colorIndex - 33) / 33;
      return interpolateColor(colors.blue, colors.purple, ratio);
    } else {
      // Transition from purple to orange
      const ratio = (colorIndex - 66) / 34;
      return interpolateColor(colors.purple, colors.orange, ratio);
    }
  };

  // Helper function to interpolate between two colors
  const interpolateColor = (color1, color2, ratio) => {
    const hex = (x) => {
      const hex = Math.round(x).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    const r1 = parseInt(color1.substring(1, 3), 16);
    const g1 = parseInt(color1.substring(3, 5), 16);
    const b1 = parseInt(color1.substring(5, 7), 16);

    const r2 = parseInt(color2.substring(1, 3), 16);
    const g2 = parseInt(color2.substring(3, 5), 16);
    const b2 = parseInt(color2.substring(5, 7), 16);

    const r = r1 + ratio * (r2 - r1);
    const g = g1 + ratio * (g2 - g1);
    const b = b1 + ratio * (b2 - b1);

    return `#${hex(r)}${hex(g)}${hex(b)}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubmitted(true);
      setTimeout(() => {
        setEmail('');
      }, 500);
    }
  };

  // Generate random digit sequence for the cyberpunk animation
  useEffect(() => {
    const generateSequence = () => {
      const length = Math.floor(Math.random() * 3) + 4; // 4-6 digits
      let result = '';
      for (let i = 0; i < length; i++) {
        result += Math.floor(Math.random() * 10);
      }
      return result;
    };

    const interval = setInterval(() => {
      setDigitSequence(generateSequence());
    }, 120); // Update every 120ms for a fast but readable effect

    return () => clearInterval(interval);
  }, []);

  // Generate code stream for the scrolling effect
  useEffect(() => {
    const generateCodeStream = () => {
      // Create a mix of binary, hex, and numbers
      const types = ['binary', 'hex', 'number'];
      const type = types[Math.floor(Math.random() * types.length)];
      
      let result = '';
      
      if (type === 'binary') {
        const length = Math.floor(Math.random() * 8) + 8;
        for (let i = 0; i < length; i++) {
          result += Math.round(Math.random());
        }
      } else if (type === 'hex') {
        const hexChars = '0123456789ABCDEF';
        result = '0x';
        const length = Math.floor(Math.random() * 4) + 4;
        for (let i = 0; i < length; i++) {
          result += hexChars[Math.floor(Math.random() * 16)];
        }
      } else {
        const length = Math.floor(Math.random() * 5) + 3;
        for (let i = 0; i < length; i++) {
          result += Math.floor(Math.random() * 10);
        }
      }
      
      return result;
    };

    const streamInterval = setInterval(() => {
      setCodeStream(generateCodeStream());
    }, 800); // Update less frequently than the digit sequence

    return () => clearInterval(streamInterval);
  }, []);

  // Countdown timer to January 1, 2027
  useEffect(() => {
    const calculateCountdown = () => {
      const targetDate = new Date('January 1, 2027 00:00:00 GMT').getTime();
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setCountdown({ days, hours, minutes, seconds });
      }
    };

    calculateCountdown();
    const countdownInterval = setInterval(calculateCountdown, 1000);

    return () => clearInterval(countdownInterval);
  }, []);

  const features = [
    {
      icon: FiShield,
      title: 'Misinformation Detection',
      description: 'Advanced AI algorithms identify false or misleading claims with precision.',
    },
    {
      icon: FiAlertTriangle,
      title: 'Bias Analysis',
      description: 'Detect subtle biases in language, framing, and narrative construction.',
    },
    {
      icon: FiTrendingUp,
      title: 'Source Credibility',
      description: 'Evaluate reliability of information sources across multiple dimensions.',
    },
    {
      icon: FiGlobe,
      title: 'Cross-Reference System',
      description: 'Compare content against verified information from trusted databases.',
    },
    {
      icon: FiCpu,
      title: 'Real-time Processing',
      description: 'Analyze content instantly for immediate verification and feedback.',
    },
    {
      icon: FiCheckCircle,
      title: 'Integrity Score',
      description: 'Quantifiable metrics to assess overall information reliability.',
    },
  ];

  // Get the current animated color
  const animatedColor = getInterpolatedColor();
  const animatedShadow = `0 0 15px ${animatedColor}`;
  
  // Create gradient with animated colors
  const animatedGradient = `linear-gradient(to right, ${colors.purple}, ${animatedColor}, ${colors.blue})`;

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
        {/* Background Effects */}
        <div className="absolute inset-0 data-grid opacity-20"></div>
        <div 
          className="absolute top-1/3 left-1/4 w-96 h-96 rounded-full blur-3xl"
          style={{ background: `${animatedColor}10` }}
        ></div>
        <div 
          className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full blur-3xl"
          style={{ background: `${animatedColor}10` }}
        ></div>

        {/* Code Stream Visual Effect */}
        <div className="absolute top-1/2 left-0 right-0 flex items-center justify-center h-16 overflow-hidden pointer-events-none">
          <div className="relative flex items-center w-full">
            <div className="absolute inset-0 flex justify-between px-4 opacity-20">
              {Array.from({ length: 15 }).map((_, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, x: -100 }}
                  animate={{ 
                    opacity: [0, 1, 0],
                    x: [i % 2 === 0 ? -200 : 200, 0, i % 2 === 0 ? 200 : -200]
                  }}
                  transition={{ 
                    duration: Math.random() * 8 + 5, 
                    repeat: Infinity,
                    delay: i * 0.7
                  }}
                  className="font-mono text-sm tracking-widest font-bold whitespace-nowrap"
                  style={{ 
                    color: i % 3 === 0 ? colors.blue : i % 3 === 1 ? colors.purple : '#ffffff',
                    textShadow: `0 0 5px ${i % 3 === 0 ? colors.blue : i % 3 === 1 ? colors.purple : '#ffffff'}`
                  }}
                >
                  {codeStream + (i % 2 === 0 ? '10' : '01') + (i % 3 === 0 ? 'FF' : 'A0')}
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Cyberpunk Digit Animation - Replaces the Tech Lines */}
        <div className="absolute top-1/3 left-0 right-0 flex justify-center items-center h-8 overflow-hidden">
          <div className="relative flex items-center">
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: '100%' }}
              transition={{ delay: 0.5, duration: 2 }}
              className="h-px w-24 absolute left-0"
              style={{ 
                background: `linear-gradient(to right, transparent, ${animatedColor}, transparent)`,
                boxShadow: `0 0 10px ${animatedColor}`
              }}
            ></motion.div>
            
            <div className="mx-4 font-mono text-2xl tracking-widest font-bold px-4 py-1 bg-black/50 backdrop-blur-sm relative z-10">
              <span 
                className="bg-clip-text text-transparent animate-pulse"
                style={{ 
                  backgroundImage: animatedGradient,
                  filter: `drop-shadow(0 0 8px ${animatedColor})`
                }}
              >
                {digitSequence}
              </span>
            </div>
            
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: '100%' }}
              transition={{ delay: 0.5, duration: 2 }}
              className="h-px w-24 absolute right-0"
              style={{ 
                background: `linear-gradient(to right, transparent, ${animatedColor}, transparent)`,
                boxShadow: `0 0 10px ${animatedColor}`
              }}
            ></motion.div>
          </div>
        </div>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.8, duration: 2 }}
          className="absolute bottom-1/3 left-0 right-0 h-px"
          style={{ 
            background: `linear-gradient(to right, ${colors.purple}, transparent, ${colors.blue})`,
            boxShadow: `0 0 10px ${animatedColor}`
          }}
        ></motion.div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 py-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8 text-center lg:text-left"
            >
              {/* Coming Soon Badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="inline-flex items-center space-x-2 px-4 py-2"
                style={{ 
                  background: `${animatedColor}20`,
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: animatedColor,
                  color: animatedColor
                }}
              >
                <SafeIcon icon={FiClock} className="w-5 h-5" />
                <span className="font-semibold">LAUNCHING 2027</span>
              </motion.div>

              {/* Main Headline */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="text-5xl md:text-7xl font-bold leading-tight"
              >
                <span 
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: animatedGradient }}
                >
                  Quinformation
                </span>
                <br />
                <span 
                  className="text-white"
                  style={{ filter: `drop-shadow(0 0 8px ${animatedColor})` }}
                >
                  Truth Has a New Interface
                </span>
              </motion.h1>

              {/* Subheadline */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="text-xl text-gray-300 max-w-2xl mx-auto lg:mx-0 leading-relaxed"
              >
                AI-Powered Media Integrity for the Modern Era. Detect bias, misinformation, and polarizing language in any
                media content with unprecedented accuracy.
              </motion.p>

              {/* Notify Me Form */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="max-w-md mx-auto lg:mx-0"
              >
                {!isSubmitted ? (
                  <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 relative">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email address"
                        className="w-full px-6 py-4 bg-gray-900 border border-gray-700 text-white placeholder-gray-400 focus:outline-none transition-colors duration-200"
                        style={{ borderColor: email ? animatedColor : 'rgba(75, 85, 99)' }}
                        required
                      />
                      <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: email ? 1 : 0 }}
                        className="absolute bottom-0 left-0 right-0 h-px"
                        style={{ backgroundImage: animatedGradient }}
                      ></motion.div>
                    </div>
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="text-black px-8 py-4 font-semibold text-lg transition-all duration-200 flex items-center justify-center space-x-2"
                      style={{ 
                        background: animatedColor,
                        boxShadow: animatedShadow
                      }}
                    >
                      <span>Notify Me</span>
                      <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                    </motion.button>
                  </form>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-green-500/20 border border-green-500 p-4 text-center"
                  >
                    <div className="flex items-center justify-center space-x-2 text-green-400">
                      <SafeIcon icon={FiCheck} className="w-6 h-6" />
                      <span className="font-semibold">You'll be notified when we launch!</span>
                    </div>
                  </motion.div>
                )}
              </motion.div>

              {/* Dynamic Countdown */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.1, duration: 1 }}
                className="pt-4"
              >
                <div className="flex justify-center lg:justify-start space-x-4">
                  {[
                    { value: countdown.days, label: 'Days' },
                    { value: countdown.hours, label: 'Hours' },
                    { value: countdown.minutes, label: 'Minutes' },
                    { value: countdown.seconds, label: 'Seconds' },
                  ].map((item, index) => (
                    <div key={index} className="text-center px-3 py-2">
                      <div 
                        className="text-3xl font-bold font-mono"
                        style={{ color: colors.blue, textShadow: `0 0 10px ${colors.blue}` }}
                      >
                        {String(item.value).padStart(2, '0')}
                      </div>
                      <div className="text-xs text-gray-400 uppercase tracking-wider">{item.label}</div>
                    </div>
                  ))}
                </div>
                <div className="text-center lg:text-left mt-2 text-sm text-gray-500">
                  Until Launch: January 1, 2027
                </div>
              </motion.div>
            </motion.div>

            {/* Mockup Visual */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 1 }}
              className="relative"
            >
              <div className="relative">
                {/* Desktop Mockup */}
                <div 
                  className="bg-gray-900 p-4 shadow-xl"
                  style={{ 
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: `${animatedColor}50`,
                    boxShadow: `0 5px 20px ${animatedColor}20`
                  }}
                >
                  <div className="aspect-video bg-gray-800 overflow-hidden relative">
                    {/* Screenshot Image */}
                    <img
                      src="https://cqpenfycgbztkozuwfiu.supabase.co/storage/v1/object/sign/logos/Screenshot%202025-08-25%20at%207.03.40%20AM.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9kZjlmMTQ4My1mMmM0LTRjZTAtOWIxNi01N2E0Njk3ZWIxYjciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJsb2dvcy9TY3JlZW5zaG90IDIwMjUtMDgtMjUgYXQgNy4wMy40MCBBTS5wbmciLCJpYXQiOjE3NTYxMjAwMzksImV4cCI6MzMyOTIxMjAwMzl9.7rYRdEM1W1qj2jn5T9W20lhc5YBMRmiJgZZazWCl9Z8"
                      alt="Quinformation Interface Screenshot"
                      className="w-full h-full object-contain"
                    />
                    {/* Simulated UI Elements */}
                    <div className="absolute top-4 left-4 flex space-x-2">
                      {[1, 2, 3].map((_, i) => (
                        <div key={i} className="w-2 h-2 bg-gray-600 rounded-full"></div>
                      ))}
                    </div>
                    <div 
                      className="absolute bottom-4 right-4 px-3 py-1 text-black text-xs font-semibold"
                      style={{ background: `linear-gradient(to right, ${colors.purple}, ${colors.blue})` }}
                    >
                      BETA
                    </div>
                  </div>
                </div>

                {/* Mobile Mockup */}
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.8, duration: 0.8 }}
                  className="absolute -bottom-10 -right-10 w-40 h-80 bg-gray-900 p-2 shadow-lg"
                  style={{ 
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: `${colors.blue}50`,
                    boxShadow: `0 5px 15px ${colors.blue}10`
                  }}
                >
                  <div className="bg-gray-800 w-full h-full relative overflow-hidden">
                    {/* Mobile Screenshot */}
                    <img
                      src="https://cqpenfycgbztkozuwfiu.supabase.co/storage/v1/object/sign/logos/Screenshot%202025-08-25%20at%207.04.00%20AM.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9kZjlmMTQ4My1mMmM0LTRjZTAtOWIxNi01N2E0Njk3ZWIxYjciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJsb2dvcy9TY3JlZW5zaG90IDIwMjUtMDgtMjUgYXQgNy4wNC4wMCBBTS5wbmciLCJpYXQiOjE3NTYxMjAzNDAsImV4cCI6MzMyOTIxMjAzNDB9.3jw0KlMiSsMUWDV50wJasrA2pP5foUqc1HADlR4Pm0M"
                      alt="Quinformation Mobile Screenshot"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                      <div className="w-8 h-1 bg-gray-600 rounded-full"></div>
                    </div>
                  </div>
                </motion.div>

                {/* Floating Elements */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute -top-5 -left-5 w-12 h-12 flex items-center justify-center"
                  style={{ 
                    background: `linear-gradient(to bottom right, ${colors.purple}, ${colors.blue})`,
                    boxShadow: `0 0 15px ${colors.purple}50`
                  }}
                >
                  <SafeIcon icon={FiShield} className="w-6 h-6 text-black" />
                </motion.div>
              </div>

              {/* Tech Accent */}
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ delay: 1.2, duration: 1.5 }}
                className="absolute -bottom-4 left-0 h-px"
                style={{ 
                  background: `linear-gradient(to right, transparent, ${animatedColor}, transparent)`,
                  boxShadow: `0 0 10px ${animatedColor}`
                }}
              ></motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gray-950 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center space-y-16"
          >
            {/* Section Header */}
            <div className="space-y-6">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                className="text-4xl md:text-5xl font-bold"
              >
                Key <span className="bg-clip-text text-transparent" style={{ backgroundImage: animatedGradient }}>Features</span>
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.8 }}
                viewport={{ once: true }}
                className="text-xl text-gray-300 max-w-3xl mx-auto"
              >
                Harnessing advanced AI to restore trust in digital information
              </motion.p>
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                  className="glass-effect p-8 space-y-6 group hover:bg-gray-900/50 transition-all duration-300 border border-gray-800"
                  style={{ 
                    borderColor: `${animatedColor}50`,
                    ":hover": { borderColor: `${colors.blue}50` }
                  }}
                >
                  {/* Icon */}
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="w-16 h-16 mx-auto flex items-center justify-center group-hover:shadow-lg transition-all duration-300"
                    style={{ 
                      background: `linear-gradient(to bottom right, ${colors.purple}, ${colors.blue})`,
                      boxShadow: `0 5px 15px ${colors.blue}20`
                    }}
                  >
                    <SafeIcon icon={feature.icon} className="w-8 h-8 text-black" />
                  </motion.div>

                  {/* Content */}
                  <div className="space-y-4">
                    <h3 
                      className="text-2xl font-bold transition-colors duration-300"
                      style={{ color: 'white', ":hover": { color: colors.blue } }}
                    >
                      {feature.title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                  </div>

                  {/* Accent Line */}
                  <motion.div
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    transition={{ delay: (index * 0.1) + 0.5, duration: 0.8 }}
                    viewport={{ once: true }}
                    className="h-px"
                    style={{ 
                      background: `linear-gradient(to right, transparent, ${colors.blue}, transparent)`,
                      boxShadow: `0 0 5px ${colors.blue}`
                    }}
                  ></motion.div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-16"
          >
            {/* Section Header */}
            <div className="text-center space-y-6">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                viewport={{ once: true }}
                className="text-4xl md:text-5xl font-bold"
              >
                How <span className="bg-clip-text text-transparent" style={{ backgroundImage: animatedGradient }}>It Works</span>
              </motion.h2>
            </div>

            {/* Process Steps */}
            <div className="relative">
              {/* Connecting Line */}
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                transition={{ delay: 0.4, duration: 1.5 }}
                viewport={{ once: true }}
                className="absolute top-24 left-0 right-0 h-px hidden md:block"
                style={{ 
                  background: `linear-gradient(to right, ${colors.purple}, ${colors.blue}, ${colors.purple}30)`,
                  boxShadow: `0 0 5px ${colors.blue}`
                }}
              ></motion.div>

              <div className="grid md:grid-cols-3 gap-8">
                {[
                  {
                    step: '01',
                    title: 'Upload',
                    description: 'Submit any media content - articles, videos, social posts - to the Quinformation platform.',
                  },
                  {
                    step: '02',
                    title: 'Analysis',
                    description: 'Our advanced AI algorithms analyze the content for bias, misinformation, and polarizing language.',
                  },
                  {
                    step: '03',
                    title: 'Results',
                    description: 'Receive a detailed integrity report with actionable insights and recommendations.',
                  },
                ].map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.3, duration: 0.8 }}
                    viewport={{ once: true }}
                    className="relative"
                  >
                    <div className="text-center space-y-6">
                      <div className="relative">
                        <div 
                          className={`w-12 h-12 md:w-16 md:h-16 text-black font-bold text-xl md:text-2xl flex items-center justify-center mx-auto z-10 relative`}
                          style={{ 
                            background: index === 0 ? colors.purple : 
                                      index === 1 ? `linear-gradient(to right, ${colors.purple}, ${colors.blue})` : 
                                      colors.blue,
                            boxShadow: `0 0 10px ${index === 0 ? colors.purple : index === 1 ? animatedColor : colors.blue}30`
                          }}
                        >
                          {step.step}
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold">{step.title}</h3>
                      <p className="text-gray-300">{step.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call-to-Action */}
      <section className="py-24 bg-gray-950 relative overflow-hidden">
        <div 
          className="absolute top-0 left-1/4 w-64 h-64 rounded-full blur-3xl"
          style={{ background: `${colors.purple}10` }}
        ></div>
        <div 
          className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl"
          style={{ background: `${colors.blue}10` }}
        ></div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="glass-effect p-8 md:p-16 text-center space-y-8 border"
            style={{ borderColor: `${animatedColor}30` }}
          >
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold"
            >
              Be Among the <span className="bg-clip-text text-transparent" style={{ backgroundImage: animatedGradient }}>First</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              viewport={{ once: true }}
              className="text-xl text-gray-300 max-w-3xl mx-auto"
            >
              Join our exclusive early access program and help shape the future of media integrity.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              viewport={{ once: true }}
              className="flex flex-col sm:flex-row justify-center gap-6 pt-4"
            >
              <motion.a
                href="#"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="text-black px-8 py-4 font-semibold text-lg transition-all duration-200 flex items-center justify-center space-x-2"
                style={{ 
                  background: colors.blue,
                  boxShadow: `0 0 15px ${colors.blue}50`
                }}
              >
                <SafeIcon icon={FiMail} className="w-5 h-5" />
                <span>Join Waitlist</span>
              </motion.a>
              <motion.a
                href="#"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="text-white px-8 py-4 font-semibold text-lg transition-all duration-200 flex items-center justify-center space-x-2"
                style={{ 
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: colors.purple,
                  ":hover": { borderColor: colors.blue, color: colors.blue }
                }}
              >
                <span>Learn More</span>
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default QuinformationPage;